import {
  IonBadge,
  IonButton,
  IonCard,
  IonCardContent,
  IonCol,
  IonGrid,
  IonIcon,
  IonLabel,
  IonRow,
  IonText,
} from "@ionic/react";
import { addOutline, removeOutline } from "ionicons/icons";
import { useState } from "react";
import { LegBlock } from "./LegBlock";
import {
  BLOCK_TYPE_FUTURE,
  BLOCK_TYPE_OPTION,
  STRATEGY_TYPE_MAP,
} from "./constants";
import { formatExpirationDate } from "./utils";
import { BlockTrade } from "./type";
import { hapticsImpactMedium } from "../../utils/core-utils/hapticUtils";

export const Block = ({ item }: { item: BlockTrade }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const getFormattedDate = (timestamp: number) => {
    const formatter = new Intl.DateTimeFormat("en-US", {
      minute: "2-digit",
      second: "2-digit",
      hour: "2-digit",
    });

    return formatter.format(new Date(timestamp));
  };

  const getTradeTypeDesc = (item: BlockTrade) => {
    let tradeType = "";
    if (item.strategyType != null && typeof item.strategyType === "string") {
      tradeType = STRATEGY_TYPE_MAP[item.strategyType];
    }
    if (
      (tradeType === "" || tradeType == null) &&
      item.legs[0]?.metadata?.type === "FUT"
    ) {
      return BLOCK_TYPE_FUTURE;
    }
    return tradeType === "" || tradeType == null
      ? BLOCK_TYPE_OPTION
      : tradeType;
  };

  const handleExpandCollapse = () => {
    if (item.mleg) {
      setIsExpanded((prev) => !prev);
      hapticsImpactMedium();
    }
  };

  const extractProductCode = () => {
    const WEEK_SUBTYPE = "WK";
    if (item.mleg) {
      return { code: item.legs[0].pcode, date: "" };
    } else {
      const leg = item.legs[0];
      let baseSymbol = leg.symbol?.split(" ")?.[0];
      if (WEEK_SUBTYPE === leg?.metadata?.subType && baseSymbol) {
        const index = baseSymbol.lastIndexOf("W");
        if (index !== -1) {
          baseSymbol = baseSymbol.substring(0, index);
        }
      }
      return formatExpirationDate(leg.pcode, leg.term, baseSymbol);
    }
  };

  const productInfo = extractProductCode();

  return (
    <IonCard
      className="trade-card ion-no-padding ion-no-margin ion-no-border"
      key={item.id}
      style={{ borderRadius: "0px", marginBottom: "10px" }}
    >
      <IonCardContent
        className="trade-content"
        onClick={() => handleExpandCollapse()}
      >
        <IonGrid className="header-grid">
          <IonRow>
            <IonCol size="11">
              <IonText className="block-title">
                <div
                  style={{
                    fontSize: "var(--one-font-size-7)",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <IonBadge
                    color="primary"
                    className="contract-code"
                    style={{
                      fontSize: "var(--one-font-size-7)",
                      flex: "none",
                      "--padding-start": "6px",
                      "--padding-end": "6px",
                    }}
                  >
                    {productInfo.code}
                  </IonBadge>
                  {productInfo.date && (
                    <span className="contract_name_sub_section_child_3">
                      {productInfo.date}
                    </span>
                  )}
                  <span>{item.legs[0].metadata?.longName}</span>
                </div>
              </IonText>
            </IonCol>
            <IonCol size="1">
              {item.legs && item.legs.length > 1 && (
                <IonButton fill="clear" size="small" className="toggle-button">
                  <IonIcon
                    icon={isExpanded ? removeOutline : addOutline}
                    className="toggle-icon-bold"
                  />
                </IonButton>
              )}
            </IonCol>
          </IonRow>
          <IonRow className="header-row">
            <IonCol size="3">
              <IonLabel style={{ fontSize: "var(--one-font-size-7)" }}>
                TIME (LOCAL)
              </IonLabel>
            </IonCol>
            <IonCol size="3">
              <IonLabel style={{ fontSize: "var(--one-font-size-7)" }}>
                {item.mleg ? "TYPE" : "C/P & STRIKE"}
              </IonLabel>
            </IonCol>
            <IonCol size="3">
              <IonLabel style={{ fontSize: "var(--one-font-size-7)" }}>
                QTY
              </IonLabel>
            </IonCol>
            <IonCol size="3">
              <IonLabel style={{ fontSize: "var(--one-font-size-7)" }}>
                {item.mleg ? "NET PRICE" : "PRICE"}
              </IonLabel>
            </IonCol>
          </IonRow>
          <IonRow className="value-row">
            <IonCol size="3">
              <IonText>
                <p style={{ fontSize: "var(--one-font-size-7)" }}>
                  {getFormattedDate(item?.lastTs)}
                </p>
              </IonText>
            </IonCol>
            <IonCol size="3">
              <IonText>
                <p style={{ fontSize: "var(--one-font-size-7)" }}>
                  {item.mleg
                    ? getTradeTypeDesc(item)
                    : `${item.legs[0].pc ?? ""} ${item.legs[0].strike ?? ""}`}
                </p>
              </IonText>
            </IonCol>
            <IonCol size="3">
              <IonText>
                <p style={{ fontSize: "var(--one-font-size-7)" }}>
                  {`${item.legs[0].qty}`}
                </p>
              </IonText>
            </IonCol>
            <IonCol size="3">
              <IonText>
                <p style={{ fontSize: "var(--one-font-size-7)" }}>
                  {item.mleg ? item.strategyPrice : item.legs[0].price}
                </p>
              </IonText>
            </IonCol>
          </IonRow>
        </IonGrid>
        {item.mleg ? (
          <LegBlock legs={item.legs} expand={isExpanded} key={item.id} />
        ) : null}
      </IonCardContent>
    </IonCard>
  );
};
